import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useAnythingTypes } from 'src/queries/anything-types';
import { Button, Input, Spinner } from 'src/UIComponents';
import { updateAnything, removeAnything } from 'src/api-services';
import {
  InputContainer,
  DropdownContainer,
  SuggestionDropdown,
  StyledSuggestionItem,
} from './AnythingAdd.styles';
import { useAnything } from 'src/queries/anything';
import { useNewAnythingHelpers } from 'src/hooks/anything-new';

const ActionsContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.md};
`;

function SuggestionItem({ value, onClick, ...rest }) {
  const handleOnclick = useCallback(() => {
    onClick(value);
  }, [onClick, value]);

  return (
    <StyledSuggestionItem onClick={handleOnclick} {...rest}>
      {value.title}
    </StyledSuggestionItem>
  );
}

function AnythingEdit({ id, ...rest }) {
  const queryClient = useQueryClient();
  const [type, setType] = useState('');
  const [typeSuggestionsOpen, setTypeSuggestionOpen] = useState(false);
  const [value1, setValue1] = useState('');
  const [value2, setValue2] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const navigate = useNavigate();
  const { getPlaceholder } = useNewAnythingHelpers();
  const { isLoading, data: anything = {} } = useAnything(id);
  const { isLoading: isLoadingTypes, data: anythingTypes = [] } =
    useAnythingTypes({
      limit: 15,
    });
  const { mutate, isLoading: isSaving } = useMutation(updateAnything, {
    onSuccess: () => {
      setType('');
      setValue1('');
      setValue2('');
      navigate(-1);
    },
  });
  const { mutate: deleteAnything, isLoading: deleting } = useMutation(
    removeAnything,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['anythings', anything.id]);
        queryClient.invalidateQueries(['somethings', anything.SomethingId]);
        navigate(`/somethings/${anything.SomethingId}`, { replace: true });
      },
    }
  );

  useEffect(() => setType(anything.type || ''), [anything.type]);
  useEffect(() => setValue1(anything.value1 || ''), [anything.value1]);
  useEffect(() => setValue2(anything.value2 || ''), [anything.value2]);

  const toggleConfirmDelete = useCallback(
    () => setConfirmOpen((prevState) => !prevState),
    []
  );
  const deleteItem = useCallback(() => {
    if (deleting) {
      return;
    }
    deleteAnything(id);
  }, [deleteAnything, deleting, id]);

  const onChangeType = useCallback((event) => {
    setType(event.target.value);
  }, []);
  const onChangeValue1 = useCallback((event) => {
    setValue1(event.target.value);
  }, []);
  const onChangeValue2 = useCallback((event) => {
    setValue2(event.target.value);
  }, []);

  const goBack = useCallback(() => navigate(-1), [navigate]);

  const doSave = useCallback(() => {
    if (isSaving || type === '') {
      return;
    }
    mutate({
      id,
      type,
      value1,
      value2,
    });
  }, [mutate, isSaving, id, type, value1, value2]);

  const openSuggestions = useCallback(() => {
    setTypeSuggestionOpen(true);
  }, []);
  const closeSuggestions = useCallback(() => {
    // Don't really understand this part but had to add a delay, otherwise it will blur and type will not be changed
    setTimeout(() => setTypeSuggestionOpen(false), 100);
  }, []);

  const placeholders = useMemo(() => {
    return getPlaceholder(type);
  }, [getPlaceholder, type]);

  const handleTypeChange = useCallback((data) => {
    setType(data.title);
  }, []);

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <div {...rest}>
      <InputContainer stack>
        <DropdownContainer>
          <Input
            value={type}
            onChange={onChangeType}
            onFocus={openSuggestions}
            onBlur={closeSuggestions}
            placeholder="Type"
          />
          <SuggestionDropdown
            style={{
              display: typeSuggestionsOpen ? 'block' : 'none',
            }}
          >
            {isLoadingTypes && <div>Loading...</div>}

            {anythingTypes.map((anythingType) => (
              <SuggestionItem
                key={anythingType.title}
                onClick={handleTypeChange}
                value={anythingType}
              />
            ))}
          </SuggestionDropdown>
        </DropdownContainer>
        <textarea
          value={value1}
          rows="4"
          onChange={onChangeValue1}
          placeholder={placeholders[0]}
          style={{
            display: type !== 'TEXT' && 'none',
          }}
        />
        <Input
          value={value1}
          onChange={onChangeValue1}
          placeholder={placeholders[0]}
          style={{
            display: type === 'TEXT' && 'none',
          }}
        />
        <Input
          value={value2}
          onChange={onChangeValue2}
          placeholder={placeholders[1]}
          style={{
            display: type === 'TEXT' && 'none',
          }}
        />
      </InputContainer>

      <ActionsContainer>
        {confirmOpen ? (
          <>
            <Button $color="danger" onClick={deleteItem}>
              Confirm delete
            </Button>
            <Button outline onClick={toggleConfirmDelete}>
              Abort
            </Button>
          </>
        ) : (
          <>
            <Button onClick={doSave} $color="primary" disabled={isLoading}>
              {isSaving ? <Spinner size="1em" /> : 'Save'}
            </Button>
            <Button disabled={isSaving} outline onClick={goBack}>
              Cancel
            </Button>
            <Button $color="danger" outline onClick={toggleConfirmDelete}>
              Delete
            </Button>
          </>
        )}
      </ActionsContainer>
    </div>
  );
}

AnythingEdit.defaultProps = {};

AnythingEdit.propTypes = {};

export default AnythingEdit;
